import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Box, Typography, Chip } from '@mui/material';
import LoadingSpinner from './LoadingSpinner';

// Virtualized data view for efficient rendering of large datasets
const VirtualizedDataView = ({ 
  data, 
  onDataSelect, 
  viewOption, 
  filterSettings,
  itemHeight = 60,
  loading = false,
  loadingProgress = 0
}) => {
  const listRef = useRef();
  const observerRef = useRef();
  const containerRef = useRef();
  const [listHeight, setListHeight] = useState(400);

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const filtered = data.filter(location => {
      // Apply filters progressively
      if (filterSettings.type === 'carbon') {
        if (location.co2e_emissions === 0) return false;
      } else {
        if (location.electricity_usage === 0) return false;
      }
      
      return true;
    });

    // Calculate total sum for percentage calculations
    const totalSum = filtered.reduce((sum, location) => {
      const value = filterSettings.type === 'carbon' 
        ? location.co2e_emissions 
        : location.electricity_usage;
      return sum + value;
    }, 0);

    // Sort by percentage in descending order (highest first)
    return filtered.sort((a, b) => {
      const valueA = filterSettings.type === 'carbon' 
        ? a.co2e_emissions 
        : a.electricity_usage;
      const valueB = filterSettings.type === 'carbon' 
        ? b.co2e_emissions 
        : b.electricity_usage;
      
      const percentageA = totalSum > 0 ? (valueA / totalSum * 100) : 0;
      const percentageB = totalSum > 0 ? (valueB / totalSum * 100) : 0;
      
      return percentageB - percentageA; // Descending order
    });
  }, [data, filterSettings.type]);

  // Calculate total sum for percentage calculations
  const totalSum = useMemo(() => {
    if (!data || data.length === 0) return 0;
    
    return data.filter(location => {
      if (filterSettings.type === 'carbon') {
        return location.co2e_emissions > 0;
      } else {
        return location.electricity_usage > 0;
      }
    }).reduce((sum, location) => {
      const value = filterSettings.type === 'carbon' 
        ? location.co2e_emissions 
        : location.electricity_usage;
      return sum + value;
    }, 0);
  }, [data, filterSettings.type]);


  // Calculate list height based on container
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        // Subtract header height (approximately 120px) and some padding
        const availableHeight = Math.max(containerHeight - 140, 200);
        setListHeight(availableHeight);
      }
    };

    updateHeight();
    // Update height on window resize
    window.addEventListener('resize', updateHeight);
    
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Handle data selection
  const handleDataSelect = useCallback((location) => {
    onDataSelect && onDataSelect(location);
  }, [onDataSelect]);

  // Render individual data item
  const renderItem = useCallback(({ index, style }) => {
    const location = filteredData[index];
    if (!location) return null;

    const value = filterSettings.type === 'carbon' 
      ? location.co2e_emissions 
      : location.electricity_usage;
    
    const percentage = totalSum > 0 ? (value / totalSum * 100).toFixed(1) : 0;
    const intensity = Math.min(percentage / 100, 1);

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          borderBottom: '1px solid #eee',
          cursor: 'pointer',
          backgroundColor: `rgba(0, 123, 255, ${intensity * 0.1})`,
          '&:hover': {
            backgroundColor: `rgba(0, 123, 255, ${intensity * 0.2})`,
          },
          transition: 'background-color 0.2s ease'
        }}
        onClick={() => handleDataSelect(location)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {location.location_of_servers.location_name}
          </Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {location.servers_at_location.length} servers
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${percentage}%`}
            size="small"
            sx={{
              backgroundColor: filterSettings.type === 'carbon' 
                ? `rgba(255, 0, 0, ${intensity})`
                : `rgba(0, 123, 255, ${intensity})`,
              color: 'white',
              fontWeight: 'bold'
            }}
          />
          
          <Typography variant="body2" sx={{ minWidth: '80px', textAlign: 'right' }}>
            {filterSettings.type === 'carbon' 
              ? `${value.toFixed(2)} tCO₂e`
              : `${value.toFixed(2)} kWh`
            }
          </Typography>
        </Box>
      </Box>
    );
  }, [filteredData, filterSettings.type, totalSum, handleDataSelect]);

  // Scroll to top when data changes
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo(0);
    }
  }, [filteredData]);

  // Intersection observer for lazy loading
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Load more data if needed
            const index = parseInt(entry.target.dataset.index);
            if (index > filteredData.length - 10) {
              // Trigger load more
              console.log('Near end of list, could load more data');
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredData.length]);

  // Show loading state when loading
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        gap: 2
      }}>
        <LoadingSpinner 
          variant="ripple" 
          size="large" 
          text="Loading data..." 
          showProgress={true}
          progress={loadingProgress * 100}
          className="text-blue-600"
        />
        <Typography variant="body2" color="textSecondary">
          Preparing data for display... {Math.round(loadingProgress * 100)}%
        </Typography>
      </Box>
    );
  }

  // Show no data message when no data is available
  if (!filteredData || filteredData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 200,
        gap: 2
      }}>
        <Typography variant="h6" color="textSecondary">
          No data available
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Try adjusting your filters or date range
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with stats */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #eee',
        backgroundColor: '#f8f9fa',
        flex: 'none'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {filteredData.length} locations
          </Typography>
          
        </Box>
        
        {/* Display total sum for verification */}
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Total: {filterSettings.type === 'carbon' 
            ? `${totalSum.toFixed(2)} tCO₂e` 
            : `${totalSum.toFixed(2)} kWh`
          }
        </Typography>
      </Box>

      {/* Virtualized list */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <List
          ref={listRef}
          height={listHeight}
          itemCount={filteredData.length}
          itemSize={itemHeight}
          width="100%"
        >
          {renderItem}
        </List>
      </Box>
    </Box>
  );
};

export default VirtualizedDataView;
